<script setup lang="ts">
import OrthoPlanForm from "@/components/models/patient/forms/OrthoProposedPlanAddEditForm.vue";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { definePage } from "unplugin-vue-router/runtime";
import QuestionnaireLayout from "@/components/models/patient/questionnaire/QuestionnaireLayout.vue";
import QuestionnaireSideBar from "@/components/models/patient/questionnaire/QuestionnaireSideBar.vue";
import { useAbility } from "@casl/vue";
import { useOrthoNeeds } from "@/composables/models/patient/useOrthoNeeds";
import { usePatient } from "@/composables/models/patient/usePatient";
import { getActualTreatmentPlan } from "@/api/models/patient/patient";
import { useCachedPatientFiles } from "@/composables/models/patient/usePatientFiles";
import { useUrlQuery } from "@/composables/shared/filters/useUrlSearch";

definePage({
  meta: {
    title: "generic.action.addEntity",
    titleParams: { entity: "generic.entity.orthoProposedPlan" },
    checkPrivileges: () => {
      const { can } = useAbility();
      return can($permActions.CREATE, $permSubjects.PATIENT__ORTHOPROPOSEDPLAN);
    },
  },
});

const { t } = useI18n();
const { patient: patientId } = useUrlQuery({ patient: String });

const { data: patient, isLoading: isPatientLoading } = usePatient(
  {
    id: patientId,
  },
  { redirectOnNotFound: true }
);
const orthoNeeds = computed(() => getActualTreatmentPlan(patient.value)?.ortho_needs?.at(-1));
const { data: orthoNeedsData, isLoading: isOrthoNeedsLoading } = useOrthoNeeds(
  {
    id: orthoNeeds,
  },
  { redirectOnNotFound: true }
);
const { patientFiles, isLoading: isPatientFilesLoading } = useCachedPatientFiles({
  patientId,
});
</script>

<template>
  <v-skeleton-loader :loading="isPatientLoading || isOrthoNeedsLoading">
    <questionnaire-layout :title="t('models.patient.orthoProposedPlan.title')">
      <ortho-plan-form
        :patient="patient"
        :ortho-needs-data="orthoNeedsData"
        :patient-id="patientId"
      />

      <template #sidebar>
        <questionnaire-side-bar
          :patient="patient"
          :patient-files="patientFiles"
          :is-loading="isPatientFilesLoading"
        />
      </template>
    </questionnaire-layout>
  </v-skeleton-loader>
</template>
